
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, KeyIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/auth/useAuth';
import { validateEmail, validatePassword, sanitizeString, sanitizeInputValue } from '@/utils/security/inputValidation';
import { rateLimiter, RATE_LIMITS } from '@/utils/security/rateLimiting';

interface LoginFormProps {
  onForgotPassword: () => void;
}

const LoginForm = ({ onForgotPassword }: LoginFormProps) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Sanitize input
    const sanitizedValue = sanitizeInputValue(value, name === 'email' ? 254 : 128);
    setLoginForm(prev => ({ ...prev, [name]: sanitizedValue }));
    if (formError) {
      setFormError('');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginForm.email || !loginForm.password) {
      setFormError(t('auth.login.errors.emailPassword'));
      return;
    }

    // Client-side validation
    const emailValidation = validateEmail(sanitizeString(loginForm.email, 254));
    if (!emailValidation.isValid) {
      setFormError(emailValidation.error || t('auth.login.errors.invalidEmail'));
      return;
    }

    const passwordValidation = validatePassword(loginForm.password);
    if (!passwordValidation.isValid) {
      setFormError(passwordValidation.error || t('auth.login.errors.invalidPassword'));
      return;
    }

    // Rate limiting check
    const rateLimitKey = `login:${loginForm.email}`;
    const rateLimitResult = rateLimiter.recordAttempt(rateLimitKey, RATE_LIMITS.LOGIN);
    
    if (!rateLimitResult.allowed) {
      const resetTime = rateLimitResult.resetTime ? new Date(rateLimitResult.resetTime).toLocaleTimeString() : 'later';
      setFormError(`${t('auth.login.errors.rateLimitExceeded')} ${resetTime}`);
      return;
    }
    
    setIsSubmitting(true);
    setFormError('');
    
    try {
      const success = await login(sanitizeString(loginForm.email, 254), loginForm.password);
      
      if (!success) {
        setFormError(t('auth.login.errors.invalidCredentials'));
      } else {
        // Reset rate limit on successful login
        rateLimiter.reset(rateLimitKey);
      }
    } catch (error: any) {
      console.error('Login form error:', error);
      setFormError(t('auth.login.errors.loginFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-3 relative">
      {formError && (
        <div className="bg-destructive/15 p-3 rounded-md text-destructive text-sm">
          {formError}
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="login-email" className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          {t('auth.login.email')}
        </Label>
        <Input 
          id="login-email" 
          name="email" 
          type="email" 
          placeholder={t('auth.login.emailPlaceholder')}
          value={loginForm.email} 
          onChange={handleLoginChange} 
          required 
          disabled={isSubmitting}
          autoComplete="email"
          autoFocus
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="login-password" className="flex items-center gap-2">
          <KeyIcon className="h-4 w-4" />
          {t('auth.login.password')}
        </Label>
        <Input 
          id="login-password" 
          name="password" 
          type="password" 
          placeholder={t('auth.login.passwordPlaceholder')}
          value={loginForm.password} 
          onChange={handleLoginChange} 
          required 
          disabled={isSubmitting}
          autoComplete="current-password"
        />
        <p className="text-xs text-muted-foreground">
          {t('auth.login.passwordRequirements')}
        </p>
      </div>
      <div className="text-right">
        <Button 
          type="button"
          variant="link" 
          size="sm" 
          className="p-0 h-auto text-kbeauty-brown"
          onClick={onForgotPassword}
          disabled={isSubmitting}
        >
          {t('auth.login.forgotPassword')}
        </Button>
      </div>
      <Button 
        type="submit" 
        className="w-full bg-kbeauty-brown hover:bg-kbeauty-brown/90 text-white" 
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('auth.login.loggingIn')}
          </>
        ) : (
          t('auth.login.signIn')
        )}
      </Button>
    </form>
  );
};

export default LoginForm;
